/**
 * Utilitaires de gestion des dates et heures avec timezone France
 * Utilise Luxon pour éviter les bugs DST et garantir les calculs en heure locale
 */

import { DateTime } from 'luxon';

const TZ = 'Europe/Paris';

/**
 * Génère une plage de dates pour un mois donné
 * @param {Date} month - Date du mois (n'importe quel jour du mois)
 * @returns {{from: string, to: string}} Plage au format YYYY-MM-DD
 */
export function getMonthRange(month) {
  const dt = DateTime.fromJSDate(month, { zone: TZ });
  const start = dt.startOf('month');
  const end = dt.endOf('month');
  
  return {
    from: start.toISODate(), // YYYY-MM-DD en heure locale FR
    to: end.toISODate()
  };
}

/**
 * Trouve le lundi de la semaine d'une date donnée
 * @param {string} dateStr - Date au format YYYY-MM-DD ou avec timestamp
 * @returns {string} Date du lundi au format YYYY-MM-DD
 */
export function getWeekMonday(dateStr) {
  // Extraire seulement YYYY-MM-DD si format avec timestamp
  const cleanDate = dateStr.split('T')[0];
  const dt = DateTime.fromISO(cleanDate, { zone: TZ });
  
  // startOf('week') retourne le lundi en ISO (Europe)
  return dt.startOf('week').toISODate();
}

/**
 * Formate une date YYYY-MM-DD en dd/MM (jour et mois uniquement)
 * @param {string} dateStr - Date au format YYYY-MM-DD
 * @returns {string} Date au format dd/MM
 */
export function formatLabelDDMM(dateStr) {
  const dt = DateTime.fromISO(dateStr, { zone: TZ });
  return dt.toFormat('dd/MM');
}

/**
 * Génère tous les lundis de semaine entre deux dates (incluses)
 * @param {string} from - Date de début (YYYY-MM-DD)
 * @param {string} to - Date de fin (YYYY-MM-DD)
 * @returns {string[]} Liste ordonnée des lundis (YYYY-MM-DD)
 */
export function buildWeekMondays(from, to) {
  const mondays = [];
  let currentMonday = getWeekMonday(from);
  const toDate = DateTime.fromISO(to, { zone: TZ });
  
  while (true) {
    mondays.push(currentMonday);
    
    // Avancer au lundi suivant (7 jours)
    const nextMonday = DateTime.fromISO(currentMonday, { zone: TZ }).plus({ days: 7 });
    currentMonday = nextMonday.toISODate();
    
    // Arrêter si on dépasse la date de fin
    if (nextMonday > toDate) break;
  }
  
  return mondays;
}

/**
 * Calcule les minutes travaillées entre check-in et check-out, moins la pause
 * Gère correctement les transitions DST
 * @param {string} checkIn - ISO timestamp check-in
 * @param {string} checkOut - ISO timestamp check-out
 * @param {number} breakMinutes - Durée de pause en minutes
 * @returns {number} Minutes travaillées
 */
export function calculateWorkMinutes(checkIn, checkOut, breakMinutes = 0) {
  const dtIn = DateTime.fromISO(checkIn, { zone: TZ });
  const dtOut = DateTime.fromISO(checkOut, { zone: TZ });
  
  // Différence en minutes en heure locale (gère DST automatiquement)
  const totalMinutes = dtOut.diff(dtIn, 'minutes').minutes;
  
  return Math.max(0, Math.round(totalMinutes - breakMinutes));
}

/**
 * Ajoute un nombre de jours à une date
 * @param {string} dateStr - Date au format YYYY-MM-DD
 * @param {number} days - Nombre de jours à ajouter (peut être négatif)
 * @returns {string} Nouvelle date au format YYYY-MM-DD
 */
export function addDays(dateStr, days) {
  const dt = DateTime.fromISO(dateStr, { zone: TZ });
  return dt.plus({ days }).toISODate();
}

/**
 * Ajoute un nombre de mois à une date
 * @param {Date} date - Date de référence
 * @param {number} months - Nombre de mois à ajouter (peut être négatif)
 * @returns {Date} Nouvelle date
 */
export function addMonths(date, months) {
  const dt = DateTime.fromJSDate(date, { zone: TZ });
  return dt.plus({ months }).toJSDate();
}

/**
 * Compare deux dates (format YYYY-MM-DD)
 * @param {string} a - Date A
 * @param {string} b - Date B
 * @returns {string} La date la plus petite
 */
export function minDate(a, b) {
  return a < b ? a : b;
}

/**
 * Compare deux dates (format YYYY-MM-DD)
 * @param {string} a - Date A
 * @param {string} b - Date B
 * @returns {string} La date la plus grande
 */
export function maxDate(a, b) {
  return a > b ? a : b;
}

/**
 * Formate un mois pour affichage (ex: "janvier 2025")
 * @param {Date} date - Date du mois
 * @returns {string} Mois formaté en français
 */
export function formatMonthDisplay(date) {
  const dt = DateTime.fromJSDate(date, { zone: TZ });
  return dt.toFormat('LLLL yyyy', { locale: 'fr' });
}

/**
 * Formate un mois pour usage comme clé (ex: "2025-01")
 * @param {Date} date - Date du mois
 * @returns {string} Mois au format YYYY-MM
 */
export function formatMonthKey(date) {
  const dt = DateTime.fromJSDate(date, { zone: TZ });
  return dt.toFormat('yyyy-MM');
}

/**
 * Crée un timestamp ISO en heure locale FR à partir d'une date et heure
 * @param {string} date - Date au format YYYY-MM-DD
 * @param {string} time - Heure au format HH:MM
 * @returns {string} ISO timestamp
 */
export function createTimestamp(date, time) {
  const dt = DateTime.fromFormat(`${date} ${time}`, 'yyyy-MM-dd HH:mm', { zone: TZ });
  return dt.toISO();
}

/**
 * Extrait l'heure d'un timestamp ISO
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Heure au format HH:MM
 */
export function extractTime(timestamp) {
  if (!timestamp) return '';
  const dt = DateTime.fromISO(timestamp, { zone: TZ });
  return dt.toFormat('HH:mm');
}

/**
 * Extrait la date d'un timestamp ISO
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Date au format YYYY-MM-DD
 */
export function extractDate(timestamp) {
  if (!timestamp) return '';
  const dt = DateTime.fromISO(timestamp, { zone: TZ });
  return dt.toISODate();
}

/**
 * Vérifie si une date est pendant une transition DST
 * @param {string} dateStr - Date au format YYYY-MM-DD
 * @returns {boolean} True si pendant transition DST
 */
export function isDSTTransition(dateStr) {
  const dt = DateTime.fromISO(dateStr, { zone: TZ });
  
  // Comparer offset à 1h du matin vs 4h du matin (couvre 2h-3h transition)
  const offsetStart = dt.set({ hour: 1 }).offset;
  const offsetEnd = dt.set({ hour: 4 }).offset;
  
  return offsetStart !== offsetEnd;
}

/**
 * Obtient la date/heure actuelle en timezone FR
 * @returns {DateTime} DateTime instance
 */
export function now() {
  return DateTime.now().setZone(TZ);
}

/**
 * Formate une durée en heures et minutes
 * @param {number} minutes - Durée en minutes
 * @returns {string} Format "Xh Ymin" ou "Xh" ou "Ymin"
 */
export function formatDuration(minutes) {
  if (minutes === 0) return '0min';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0 && mins > 0) return `${hours}h ${mins}min`;
  if (hours > 0) return `${hours}h`;
  return `${mins}min`;
}
